import { DataProvider, HttpError } from '@refinedev/core';
import { acquireAdminToken } from '../auth/msalConfig';

/**
 * Refine-shaped data provider against the viq-backend admin
 * endpoints. The shape is REST-like (`/v1/admin/<resource>` for
 * collections, `/v1/admin/<resource>/:id` for items) but a few
 * endpoints diverge (toolkit is keyed by conference_id, users by
 * oid). Those are handled in dedicated hooks/components, not here.
 *
 * Two Refine-specific shape contracts the SPA depends on:
 *
 *   1. `getList(resource)` must return `{ data: T[], total: number }`.
 *      The backend's list endpoints return `{ <resource>: T[] }` (no
 *      cursor for full lists). We unwrap and synthesize `total`
 *      from `data.length` since the backend's full-list endpoints
 *      don't paginate. The Leads viewer paginates differently and
 *      bypasses this provider entirely (TanStack Table + a dedicated
 *      hook in src/views/leads).
 *
 *   2. Errors must be a Refine `HttpError` (`{ statusCode, message,
 *      errors? }`). The backend's `{ error: { code, message,
 *      details } }` envelope is translated here; `details.fieldErrors`
 *      hydrates `useForm`'s inline validation.
 */

const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  ''; // empty → uses Vite dev proxy

const RESOURCE_COLLECTION_KEY: Record<string, string> = {
  companies: 'companies',
  conferences: 'conferences',
  users: 'users',
};

interface BackendError {
  error: {
    code: string;
    message: string;
    details?: { fieldErrors?: Record<string, string[]> };
  };
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await acquireAdminToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function fetchJson<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = await authHeaders();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers as Record<string, string>) },
  });
  if (!response.ok) {
    let body: BackendError | null = null;
    try {
      body = (await response.json()) as BackendError;
    } catch {
      // non-JSON error body — fall through with synthetic message
    }
    const httpError: HttpError & { retryAfter?: number } = {
      statusCode: response.status,
      message: body?.error?.message ?? response.statusText,
      errors: body?.error?.details?.fieldErrors,
    };
    // A 429 carries `Retry-After` (seconds until the caller's next token —
    // see the backend's rateLimit middleware). Kept on the error so a caller
    // that fans out per-lead writes can wait the window out instead of
    // hammering; every other caller ignores it.
    const retryAfter = Number(response.headers.get('Retry-After'));
    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      httpError.retryAfter = retryAfter;
    }
    throw httpError;
  }
  if (response.status === 204) {
    return undefined as unknown as T;
  }
  return (await response.json()) as T;
}

function resourcePath(resource: string, id?: string | number): string {
  // Users are keyed by `oid` in the URL; Refine still calls the id
  // field "id" semantically.
  return id !== undefined
    ? `/v1/admin/${resource}/${id}`
    : `/v1/admin/${resource}`;
}

function unwrapList<T>(
  resource: string,
  body: Record<string, unknown>,
): { data: T[]; total: number } {
  const key = RESOURCE_COLLECTION_KEY[resource] ?? resource;
  const data = ((body[key] as T[]) ?? []) as T[];
  return { data, total: data.length };
}

export const dataProvider: DataProvider = {
  getApiUrl: () => API_BASE_URL,

  getList: async ({ resource, meta }) => {
    const archived =
      (meta as { archived?: boolean } | undefined)?.archived === true;
    const path = resourcePath(resource) + (archived ? '?archived=true' : '');
    const body = await fetchJson<Record<string, unknown>>(path);
    return unwrapList(resource, body);
  },

  getOne: async ({ resource, id }) => {
    const data = await fetchJson<Record<string, unknown>>(
      resourcePath(resource, id as string),
    );
    return { data: data as never };
  },

  create: async ({ resource, variables }) => {
    const data = await fetchJson<Record<string, unknown>>(
      resourcePath(resource),
      {
        method: 'POST',
        body: JSON.stringify(variables),
      },
    );
    return { data: data as never };
  },

  update: async ({ resource, id, variables }) => {
    const data = await fetchJson<Record<string, unknown>>(
      resourcePath(resource, id as string),
      {
        method: 'PATCH',
        body: JSON.stringify(variables),
      },
    );
    return { data: data as never };
  },

  deleteOne: async ({ resource, id }) => {
    await fetchJson<void>(resourcePath(resource, id as string), {
      method: 'DELETE',
    });
    return { data: { id } as never };
  },

  // Required by the DataProvider interface; the admin SPA doesn't use
  // these surfaces, so throw early instead of silently misbehaving.
  getMany: async () => {
    throw new Error('getMany is not implemented; use useMany with mode:"server" + getOne per id, or build a dedicated list endpoint');
  },
  createMany: async () => {
    throw new Error('createMany is not implemented');
  },
  deleteMany: async () => {
    throw new Error('deleteMany is not implemented');
  },
  updateMany: async () => {
    throw new Error('updateMany is not implemented');
  },
  custom: async ({ url, method, payload, headers }) => {
    const data = await fetchJson<Record<string, unknown>>(url, {
      method: method ?? 'GET',
      ...(payload ? { body: JSON.stringify(payload) } : {}),
      ...(headers
        ? { headers: headers as unknown as Record<string, string> }
        : {}),
    });
    return { data: data as never };
  },
};

/**
 * Direct call helper for endpoints that don't fit the Refine
 * resource shape (toolkit by conferenceId, live feed, assignments
 * bulk endpoints, leads with cursor pagination). Same auth +
 * error-translation as `dataProvider`.
 */
export async function adminFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  return fetchJson<T>(path, init);
}

/**
 * Variant of adminFetch that returns the raw fetch Response instead
 * of a parsed JSON body. Used by the CSV/xlsx export flow where the
 * caller needs the response stream + filename from Content-Disposition,
 * not a parsed body. Same auth + error envelope as adminFetch — on
 * non-OK responses the JSON error is parsed and thrown as HttpError.
 */
export async function adminFetchRaw(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = await authHeaders();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers as Record<string, string>) },
  });
  if (!response.ok) {
    // Mirror fetchJson's error translation for consistency with the
    // rest of the SPA's error UX.
    const text = await response.text();
    let message = `Request failed with status ${response.status}`;
    try {
      const body = JSON.parse(text) as BackendError;
      message = body.error?.message ?? message;
    } catch {
      /* not JSON — keep the default */
    }
    throw { statusCode: response.status, message } as HttpError;
  }
  return response;
}
